/**
 * Shared parent-child wrapper for assignment flows that combine:
 * - a shared select control for choosing records
 * - a shared table preview for already selected records
 *
 * PARENT-CHILD CONTRACT:
 * Parents own the authoritative selected IDs and row data.
 * This wrapper only normalizes emitted selections and forwards row actions.
 */
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

import { UiSelectComponent } from '../ui-select/ui-select.component';
import { UiSelectOption } from '../ui-select/ui-select.types';
import { UiTableComponent } from '../ui-table/ui-table.component';
import { UiTableAction, UiTableActionClick, UiTableBadgeRule, UiTableColumn } from '../ui-table/ui-table.types';
import { UiEmptyStateComponent } from '../ui-empty-state/ui-empty-state.component';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

@Component({
  selector: 'ui-assignment-selector',
  standalone: true,
  imports: [UiSelectComponent, UiTableComponent, UiEmptyStateComponent, UiLabelTranslatePipe],
  templateUrl: './ui-assignment-selector.component.html',
  styleUrl: './ui-assignment-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiAssignmentSelectorComponent {
  // PARENT-CHILD CONTRACT: parents provide labels, options, and selected IDs.
  readonly label = input('');
  readonly required = input(false);
  readonly single = input(false);
  readonly values = input<string[]>([]);
  readonly options = input<UiSelectOption[]>([]);
  readonly error = input('');
  readonly hint = input('');
  readonly selectedRows = input<unknown[]>([]);
  readonly columns = input<UiTableColumn[]>([]);
  readonly emptyMessage = input('No records selected yet.');
  readonly emptyStateTitle = input('shared.table.noData');
  readonly emptyStateDescription = input('shared.emptyState.selectToPreview');
  readonly rowActions = input<UiTableAction[]>([]);
  readonly badgeRules = input<Record<string, UiTableBadgeRule> | null>(null);
  readonly showPagination = input(false);
  readonly defaultPageSize = input(10);
  readonly loadMoreOnScroll = input(false);
  readonly loadingMoreOptions = input(false);
  readonly hasMoreOptions = input(false);

  readonly valuesChange = output<string[]>();
  readonly actionClick = output<UiTableActionClick>();
  readonly loadMoreOptions = output<void>();

  /**
   * Normalizes the single-value select output into the array contract used by the parent.
   */
  onValueChange(value: string): void {
    this.valuesChange.emit(value ? [value] : []);
  }

  /**
   * Keeps the emitted value shape consistent for both single and multi assignment flows.
   * BUSINESS LOGIC: in single mode only the last chosen record is kept active.
   */
  onValuesChange(values: string[]): void {
    // In single mode keep only the last selected value so the dropdown acts as a single-select.
    this.valuesChange.emit(this.single() && values.length > 1 ? [values[values.length - 1]] : values);
  }

  /**
   * Forwards table row actions so the parent decides how to remove or inspect assignments.
   */
  onActionClick(event: UiTableActionClick): void {
    this.actionClick.emit(event);
  }

  /**
   * Forwards dropdown paging requests so feature parents keep API pagination state.
   */
  onLoadMoreOptions(): void {
    this.loadMoreOptions.emit();
  }
}
